// =============================================================
// Axios API client + shared auth/domain types (co-located)
// =============================================================
import axios, { type AxiosError } from "axios";

// Empty string => same-origin. The Bun dev/prod server proxies /api/*
// to the PHP backend, so the SPA always talks same-origin.
const baseURL = (import.meta as any).env?.VITE_API_URL || "";

export const api = axios.create({
  baseURL,
  withCredentials: true, // send the session cookie (impact_session)
  headers: { "Content-Type": "application/json" },
});

// --- Shared domain enums + types (co-located here per project choice) ----
export type Role = "AGENT" | "IT" | "ADMIN";
export type Priority = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
export type Status = "OPEN" | "IN_PROGRESS" | "ON_HOLD" | "RESOLVED" | "CLOSED";
export type Category =
  | "SYSTEM"
  | "HARDWARE"
  | "SOFTWARE"
  | "NETWORK"
  | "ACCESS"
  | "OTHER";
export type SlaState = "ok" | "at-risk" | "breached" | "met" | "unknown";

export interface User {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  phone_number?: string | null;
  role: Role;
  department?: string | null;
  additionalDepartments?: string[] | null;
  isActive?: boolean;
  lastLoginAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserSummary {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  phone_number?: string | null;
  role: Role;
  department?: string | null;
  additionalDepartments?: string[] | null;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  priority: Priority;
  status: Status;
  category: Category;
  issue?: string | null;
  isDeleted: boolean;
  slaDeadline?: string | null;
  resolvedAt?: string | null;
  resolution?: string | null;
  submitterId: string;
  assigneeId?: string | null;
  submitter?: UserSummary | null;
  assignee?: UserSummary | null;
  slaState?: SlaState;
  createdAt: string;
  updatedAt: string;
  _count?: { comments: number; attachments: number };
  attachments?: Attachment[];
}

export interface Comment {
  id: string;
  content: string;
  isInternal: boolean;
  ticketId: string;
  authorId: string;
  author?: UserSummary | null;
  createdAt: string;
}

export interface Attachment {
  id: string;
  filename: string;
  storedName?: string;
  mimeType: string;
  size: number;
  ticketId?: string;
  uploaderId: string;
  createdAt: string;
}

export interface ActivityLog {
  id: string;
  action: string;
  meta?: Record<string, unknown> | null;
  ticketId: string;
  userId: string;
  user?: UserSummary | null;
  ticket?: { id: string; title: string; status: Status } | null;
  createdAt: string;
}

export interface NotificationItem {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  imageUrl?: string | null;
  authorId: string;
  authorName: string;
  authorAvatar?: string | null;
  createdAt: string;
  updatedAt: string;
  comments?: AnnouncementComment[];
}

export interface AnnouncementComment {
  id: string;
  content: string;
  announcementId: string;
  authorId: string;
  authorName: string;
  authorAvatar?: string | null;
  authorRole: Role;
  createdAt: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// --- Global 401 handler hook -------------------------------------------
let onUnauthorized: ((err: AxiosError) => void) | null = null;
export function setUnauthorizedHandler(fn: ((err: AxiosError) => void) | null) {
  onUnauthorized = fn;
}

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      onUnauthorized?.(err);
    }
    return Promise.reject(err);
  },
);

/** Extract a human message from an axios error (matches the PHP {message}). */
export function apiError(err: unknown): string {
  const e = err as AxiosError<{ message?: string }>;
  return e?.response?.data?.message || e?.message || "Something went wrong";
}
